<template>
    <div class="invoices-layout">
        <FolderSidebar :invoice-counts="invoiceCounts" />

        <div class="invoices-layout__main">
            <div class="row">
                <div class="col-12 mb-4 pr-0 d-flex justify-content-between">
                    <div>
                        <h4 class="mb-0">{{ $t('title') }}</h4>
                        <small class="text-muted" v-if="activeFolderPath">{{ activeFolderPath }}</small>
                    </div>
                    <div>
                        <button class="btn btn-sm btn-outline-dark"
                                :class="{ 'mr-3': !isStorageLocal }"
                                @click="createNewInvoice">{{ $t('new_invoice') }}
                        </button>
                        <button class="btn btn-sm btn-outline-secondary mr-2"
                                @click="openAssignModal"
                                :disabled="selectedInvoiceIds.length === 0">
                            {{ $t('folders:add_to_folder') }}
                        </button>
                        <b-dropdown variant="link" size="sm" no-caret right>
                            <template slot="button-content">
                                <i class="material-icons">more_vert</i>
                            </template>
                            <b-dropdown-item @click="exportJson">{{ $t('export') }}</b-dropdown-item>
                            <b-dropdown-item @click="openImportModal">{{ $t('import') }}</b-dropdown-item>
                            <b-dropdown-item v-if="isHttpStorage" @click="logout">Logout</b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <InvoicesList
                        :active-folder-id="activeFolderId"
                        @selection-change="selectedInvoiceIds = $event"
                    />
                </div>
            </div>
        </div>

        <FolderAssignModal
            v-if="showAssignModal"
            :invoice-ids="selectedInvoiceIds"
            @close="showAssignModal = false"
            @assigned="onAssigned"
        />
    </div>
</template>

<script>
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import { mapGetters } from 'vuex';
import InvoicesList from '@/components/invoices/InvoicesList';
import FolderSidebar from '@/components/folders/FolderSidebar';
import FolderAssignModal from '@/components/folders/FolderAssignModal';
import Invoice from '@/store/models/invoice';
import Folder from '@/store/models/folder';
import config from '@/config/app.config';
import AuthService from '@/services/auth.service';

export default {
  name: 'invoices',
  i18nOptions: { namespaces: 'invoices' },
  components: {
    InvoicesList,
    FolderSidebar,
    FolderAssignModal,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      selectedInvoiceIds: [],
      showAssignModal: false,
    };
  },
  computed: {
    ...mapGetters({
      team: 'teams/team',
      activeFolderId: 'folders/activeFolderId',
    }),
    isHttpStorage() {
      return config.storageType === 'http';
    },
    isStorageLocal() {
      return config.storageType !== 'http';
    },
    allFolders() {
      return Folder.query().where('$isNew', false).get();
    },
    allInvoices() {
      return Invoice.query().where('$isNew', false).with(['client']).get();
    },
    invoiceCounts() {
      const counts = {};
      this.allInvoices.forEach((inv) => {
        if (inv.folder_ids && Array.isArray(inv.folder_ids)) {
          inv.folder_ids.forEach((fid) => {
            counts[fid] = (counts[fid] || 0) + 1;
          });
        }
      });
      return counts;
    },
    activeFolderPath() {
      if (!this.activeFolderId) return '';
      const parts = [];
      let current = Folder.find(this.activeFolderId);
      while (current) {
        parts.unshift(current.name);
        current = current.parent_id ? Folder.find(current.parent_id) : null;
      }
      return parts.join(' / ');
    },
  },
  methods: {
    createNewInvoice() {
      this.$store.dispatch('invoices/createNewInvoice')
        .then(id => this.$router.push({ name: 'invoice', params: { id } }))
        .catch(() => {});
    },
    exportJson() {
      this.$store.dispatch('data/exportJson');
    },
    openImportModal() {
      this.$store.commit('data/isImportModalOpen', true);
    },
    openAssignModal() {
      this.showAssignModal = true;
    },
    onAssigned() {
      this.showAssignModal = false;
      this.selectedInvoiceIds = [];
    },
    logout() {
      AuthService.logout();
      this.$router.push({ name: 'login' });
    },
  },
};
</script>

<style lang="scss" scoped>
.invoices-layout {
    display: flex;
    min-height: calc(100vh - 160px);

    &__main {
        flex: 1;
        padding: 0 16px;
        overflow: hidden;
    }
}
</style>
